import React, { useEffect, useRef } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { ChartSkeleton } from "src/components/skeleton";
import { ChartColors } from "../useChartColors";
import {
  FleetHealth,
  HealthStatus,
  HEALTH_COLOR,
  HEALTH_ICON,
} from "../health";
import { FleetHealthEntry } from "../useFleetHealth";
import { OpsanioHardwareKind, OpsanioHardwareSummary } from "src/auth";

/**
 * Theme-aware chart.js canvas. Recreates the chart whenever the serialized
 * config changes (data or - via useChartColors - the active color mode), and
 * always destroys the instance on unmount to avoid canvas leaks.
 */
const ChartCanvas: React.FC<{ config: any; height: number }> = ({
  config,
  height,
}) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);
  const configKey = JSON.stringify(config);

  useEffect(() => {
    let active = true;
    import("chart.js/auto").then(({ default: Chart }) => {
      if (!active || !ref.current) return;
      if (chartRef.current) chartRef.current.destroy();
      chartRef.current = new Chart(ref.current, config);
    });
    return () => {
      active = false;
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  return (
    <div style={{ height }}>
      <canvas ref={ref} aria-hidden="true" />
    </div>
  );
};

const ChartPanel: React.FC<{
  title: string;
  loading?: boolean;
  empty?: boolean;
  emptyText?: string;
  height?: number;
  children?: React.ReactNode;
}> = ({ title, loading, empty, emptyText, height = 200, children }) => {
  const { translate } = useAnketaI18n();
  return (
    <EuiPanel hasBorder paddingSize="m" style={{ height: "100%" }}>
      <EuiTitle size="xxs">
        <h4>{title}</h4>
      </EuiTitle>
      <EuiSpacer size="s" />
      {loading ? (
        <ChartSkeleton height={height} />
      ) : empty ? (
        <EuiFlexGroup
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: height }}
          responsive={false}
        >
          <EuiFlexItem grow={false}>
            <EuiText size="s" color="subdued">
              {emptyText ??
                translate("anketa.opsanio.overview.chart.noData", {
                  defaultMessage: "No data to display",
                })}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ) : (
        children
      )}
    </EuiPanel>
  );
};

const baseLegend = (c: ChartColors) => ({
  labels: { color: c.text, boxWidth: 12, padding: 10, font: { size: 11 } },
});
const baseTooltip = (c: ChartColors) => ({
  backgroundColor: c.tooltipBg,
  titleColor: c.text,
  bodyColor: c.text,
  borderColor: c.grid,
  borderWidth: 1,
});

/**
 * Honest fleet status distribution as a single stacked bar.
 *
 * Replaces the former donut: same four honest buckets (unmonitored is an
 * explicit segment, never folded into healthy), but each legend row carries the
 * status icon as well as its color - status is never communicated by color
 * alone - plus the absolute count and its share of the fleet. Drawn with OUI /
 * CSS rather than a canvas, so it costs no chart instance.
 */
export const FleetStatusStackedBar: React.FC<{
  fleet: FleetHealth;
  colors: ChartColors;
  loading?: boolean;
}> = ({ fleet, colors, loading }) => {
  const { translate } = useAnketaI18n();
  const sc = fleet.statusCounts;
  const label: Record<HealthStatus, string> = {
    healthy: translate("anketa.opsanio.overview.status.healthy", {
      defaultMessage: "Healthy",
    }),
    warning: translate("anketa.opsanio.overview.status.warning", {
      defaultMessage: "Warning",
    }),
    critical: translate("anketa.opsanio.overview.status.critical", {
      defaultMessage: "Critical",
    }),
    unknown: translate("anketa.opsanio.overview.status.unmonitored", {
      defaultMessage: "Unmonitored",
    }),
  };
  const order: HealthStatus[] = ["healthy", "warning", "critical", "unknown"];
  const rows = order.map((key) => ({
    key,
    label: label[key],
    count: sc[key],
    pct: fleet.total > 0 ? Math.round((100 * sc[key]) / fleet.total) : 0,
  }));
  const present = rows.filter((r) => r.count > 0);

  return (
    <ChartPanel
      title={translate("anketa.opsanio.overview.chart.fleetStatus", {
        defaultMessage: "Fleet status distribution",
      })}
      loading={loading}
      empty={present.length === 0}
    >
      <div
        style={{
          display: "flex",
          height: 16,
          borderRadius: 3,
          overflow: "hidden",
        }}
        role="img"
        aria-label={present
          .map((r) => `${r.label}: ${r.count}`)
          .join(", ")}
      >
        {present.map((r) => (
          <div
            key={r.key}
            style={{
              flexGrow: r.count,
              backgroundColor: colors.health[r.key],
            }}
            title={`${r.label}: ${r.count} (${r.pct}%)`}
          />
        ))}
      </div>
      <EuiSpacer size="m" />
      <EuiFlexGroup direction="column" gutterSize="xs">
        {rows.map((r) => (
          <EuiFlexItem key={r.key} grow={false}>
            <EuiFlexGroup
              gutterSize="s"
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiIcon
                  type={HEALTH_ICON[r.key]}
                  color={HEALTH_COLOR[r.key]}
                  size="s"
                />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs">{r.label}</EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiText size="xs">
                  <strong>{r.count}</strong>{" "}
                  <span style={{ opacity: 0.7 }}>({r.pct}%)</span>
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>
    </ChartPanel>
  );
};

/** Current open incidents per appliance (NOT a fabricated time trend). */
export const OpenIncidentsBar: React.FC<{
  byId: Record<string, FleetHealthEntry>;
  colors: ChartColors;
  loading?: boolean;
}> = ({ byId, colors, loading }) => {
  const { translate } = useAnketaI18n();
  const rows = Object.values(byId)
    .filter((e) => e.health.activeIncidents > 0)
    .sort((a, b) => b.health.activeIncidents - a.health.activeIncidents)
    .slice(0, 12);

  const title = translate("anketa.opsanio.overview.chart.openIncidents", {
    defaultMessage: "Open incidents by appliance",
  });

  return (
    <ChartPanel
      title={title}
      loading={loading}
      empty={rows.length === 0}
      emptyText={translate("anketa.opsanio.overview.chart.noIncidents", {
        defaultMessage: "No open incidents across the fleet",
      })}
    >
      <ChartCanvas
        height={200}
        config={{
          type: "bar",
          data: {
            labels: rows.map(
              (e) => e.appliance.displayName || e.appliance.id,
            ),
            datasets: [
              {
                label: title,
                data: rows.map((e) => e.health.activeIncidents),
                backgroundColor: rows.map((e) =>
                  e.health.status === "critical"
                    ? colors.danger
                    : colors.warning,
                ),
                borderRadius: 3,
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: baseTooltip(colors),
            },
            scales: {
              x: {
                ticks: { color: colors.textSubdued, font: { size: 10 }, precision: 0 },
                grid: { color: colors.grid },
                beginAtZero: true,
              },
              y: {
                ticks: { color: colors.text, font: { size: 10 } },
                grid: { color: "transparent" },
              },
            },
          },
        }}
      />
    </ChartPanel>
  );
};

/** Real provisioning data: appliances per wave. */
export const WaveDistributionDonut: React.FC<{
  waveCounts: Record<string, number>;
  colors: ChartColors;
}> = ({ waveCounts, colors }) => {
  const { translate } = useAnketaI18n();
  const entries = Object.entries(waveCounts).filter(([, v]) => v > 0);
  return (
    <ChartPanel
      title={translate("anketa.opsanio.overview.chart.waveDistribution", {
        defaultMessage: "Wave distribution",
      })}
      empty={entries.length === 0}
    >
      <ChartCanvas
        height={200}
        config={{
          type: "doughnut",
          data: {
            labels: entries.map(([k]) => k),
            datasets: [
              {
                data: entries.map(([, v]) => v),
                backgroundColor: entries.map(
                  ([k]) => colors.wave[k] ?? colors.subdued,
                ),
                borderColor: colors.surface,
                borderWidth: 2,
                hoverOffset: 6,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "66%",
            plugins: {
              legend: { position: "right", ...baseLegend(colors) },
              tooltip: baseTooltip(colors),
            },
          },
        }}
      />
    </ChartPanel>
  );
};

/** Real provisioning data: agents per appliance. */
export const AgentsBar: React.FC<{
  rows: Array<{ label: string; agents: number; wave: string }>;
  colors: ChartColors;
}> = ({ rows, colors }) => {
  const { translate } = useAnketaI18n();
  const sorted = [...rows].sort((a, b) => b.agents - a.agents).slice(0, 12);
  return (
    <ChartPanel
      title={translate("anketa.opsanio.overview.chart.agentsPerAppliance", {
        defaultMessage: "Agents per appliance",
      })}
      empty={sorted.length === 0}
    >
      <ChartCanvas
        height={200}
        config={{
          type: "bar",
          data: {
            labels: sorted.map((r) => r.label),
            datasets: [
              {
                label: translate("anketa.opsanio.overview.chart.agents", {
                  defaultMessage: "Agents",
                }),
                data: sorted.map((r) => r.agents),
                backgroundColor: sorted.map(
                  (r) => colors.wave[r.wave] ?? colors.primary,
                ),
                borderRadius: 3,
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: baseTooltip(colors),
            },
            scales: {
              x: {
                ticks: { color: colors.textSubdued, font: { size: 10 }, precision: 0 },
                grid: { color: colors.grid },
                beginAtZero: true,
              },
              y: {
                ticks: { color: colors.text, font: { size: 10 } },
                grid: { color: "transparent" },
              },
            },
          },
        }}
      />
    </ChartPanel>
  );
};

/**
 * Real NetBox data: how much of the hardware pool is assigned to appliances vs.
 * still free, per kind. Kinds NetBox could not answer for are omitted (their
 * counts are unknown, not zero) - the panel above names them.
 */
export const HardwareAllocationBar: React.FC<{
  summary: OpsanioHardwareSummary;
  colors: ChartColors;
  loading?: boolean;
  /** Kinds whose lookup failed; excluded from the chart. */
  failed?: OpsanioHardwareKind[];
}> = ({ summary, colors, loading, failed = [] }) => {
  const { translate } = useAnketaI18n();
  const kinds: Array<{ key: OpsanioHardwareKind; label: string }> = [
    {
      key: "server",
      label: translate("anketa.opsanio.overview.hardware.kind.server", {
        defaultMessage: "Servers",
      }),
    },
    {
      key: "switch",
      label: translate("anketa.opsanio.overview.hardware.kind.switch", {
        defaultMessage: "Switches",
      }),
    },
    {
      key: "dac",
      label: translate("anketa.opsanio.overview.hardware.kind.dac", {
        defaultMessage: "DAC cables",
      }),
    },
  ];
  const rows = kinds.filter(
    (k) => !failed.includes(k.key) && summary[k.key].total > 0,
  );

  const inUseLabel = translate("anketa.opsanio.overview.hardware.inUse", {
    defaultMessage: "In use",
  });
  const freeLabel = translate("anketa.opsanio.overview.hardware.free", {
    defaultMessage: "Free",
  });
  const failedLabel = translate("anketa.opsanio.overview.hardware.failed", {
    defaultMessage: "Defective",
  });

  return (
    <ChartPanel
      title={translate("anketa.opsanio.overview.chart.hardwareAllocation", {
        defaultMessage: "Hardware allocation",
      })}
      loading={loading}
      empty={rows.length === 0}
      emptyText={translate("anketa.opsanio.overview.hardware.empty", {
        defaultMessage: "No opsanio hardware found in NetBox",
      })}
    >
      <ChartCanvas
        height={200}
        config={{
          type: "bar",
          data: {
            labels: rows.map((r) => r.label),
            datasets: [
              {
                label: inUseLabel,
                data: rows.map((r) => summary[r.key].inUse),
                backgroundColor: colors.primary,
                borderRadius: 3,
              },
              {
                label: freeLabel,
                data: rows.map((r) => summary[r.key].free),
                backgroundColor: colors.success,
                borderRadius: 3,
              },
              // Defective (NetBox status failed) - neither assigned nor free.
              {
                label: failedLabel,
                data: rows.map((r) => summary[r.key].failed ?? 0),
                backgroundColor: colors.danger,
                borderRadius: 3,
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "bottom", ...baseLegend(colors) },
              tooltip: baseTooltip(colors),
            },
            scales: {
              x: {
                stacked: true,
                ticks: {
                  color: colors.textSubdued,
                  font: { size: 10 },
                  precision: 0,
                },
                grid: { color: colors.grid },
                beginAtZero: true,
              },
              y: {
                stacked: true,
                ticks: { color: colors.text, font: { size: 10 } },
                grid: { color: "transparent" },
              },
            },
          },
        }}
      />
    </ChartPanel>
  );
};
